import { useCallback, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Paper, Badge, ActionIcon } from '@mantine/core'
import { IconGripVertical, IconHeart, IconFlask, IconPencil, IconTrash, IconUserUp } from '@tabler/icons-react'
import type { SeatingGroup } from '@/lib/seatingUtils'
import { getGroupLabel } from '@/lib/seatingUtils'
import styles from './SeatingChart.module.css'

interface GuestGroupCardProps {
  group: SeatingGroup
  isDragging?: boolean
  onEditTestGuest?: (group: SeatingGroup) => void
  onDeleteTestGuest?: (group: SeatingGroup) => void
  onPromoteTestGuest?: (group: SeatingGroup) => void
}

export function GuestGroupCard({
  group,
  isDragging,
  onEditTestGuest,
  onDeleteTestGuest,
  onPromoteTestGuest,
}: GuestGroupCardProps) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: group.id,
  })
  const [actionsOpen, setActionsOpen] = useState(false)

  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined
  const showTestActions = group.isTestGuest && !isDragging
  const displayText = group.additionalLabel
    ? `${group.displayName} + ${group.additionalLabel}`
    : group.displayName

  const handleCardClick = useCallback(
    (event: React.MouseEvent) => {
      if (!showTestActions) return
      const target = event.target as HTMLElement
      if (target.closest(`.${styles.dragHandle}`) || target.closest(`.${styles.testGuestActions}`)) {
        return
      }
      setActionsOpen((open) => !open)
    },
    [showTestActions]
  )

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      px="xs"
      py={4}
      shadow={isDragging ? 'md' : 'xs'}
      withBorder
      onClick={handleCardClick}
      className={`${styles.guestGroup} ${group.isCoupleMember ? styles.coupleGroup : ''} ${group.isTestGuest ? styles.testGuestGroup : ''} ${isDragging ? styles.guestGroupDragging : ''} ${actionsOpen ? styles.guestGroupActionsOpen : ''}`}
    >
      <div className={styles.guestGroupRow}>
        <button
          type="button"
          className={styles.dragHandle}
          {...listeners}
          {...attributes}
          aria-label={`${group.displayName} verschieben`}
        >
          <IconGripVertical size={14} />
        </button>

        {group.isCoupleMember && (
          <IconHeart size={14} className={styles.coupleIcon} aria-hidden="true" />
        )}

          {group.isTestGuest && (
            <Badge
              size="xs"
              variant="light"
              color="grape"
              className={styles.testBadge}
              title="Testnutzer"
              aria-label="Testnutzer"
            >
              <IconFlask size={10} aria-hidden="true" />
            </Badge>
          )}

        <div className={styles.guestGroupContent}>
          <span className={styles.guestGroupText} title={displayText}>
            {displayText}
          </span>

          {showTestActions && (
            <div
              className={styles.testGuestActions}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {onPromoteTestGuest && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="green"
                  onClick={() => onPromoteTestGuest(group)}
                  aria-label={`${group.displayName} zu echtem Gast promoten`}
                  title="Zu echtem Gast promoten"
                >
                  <IconUserUp size={14} />
                </ActionIcon>
              )}
              {onEditTestGuest && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => onEditTestGuest(group)}
                  aria-label={`${group.displayName} bearbeiten`}
                  title="Bearbeiten"
                >
                  <IconPencil size={14} />
                </ActionIcon>
              )}
              {onDeleteTestGuest && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() => onDeleteTestGuest(group)}
                  aria-label={`${group.displayName} entfernen`}
                  title="Entfernen"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </div>
          )}
        </div>

        <Badge size="md" variant="light" color="yellow" className={styles.guestBadge}>
          {group.personCount}
        </Badge>
      </div>

      <span className={styles.srOnly}>{getGroupLabel(group)}</span>
    </Paper>
  )
}
